<template>
    <button
        class="flex gap-1 items-center"
        :aria-label="toggleText"
        :class="textPosition == 'left' ? 'flex-row' : 'flex-row-reverse'"
        @click.prevent="toggleWishlist"
    >
        <div v-if="toggleText">
            {{ toggleText }}
        </div>

        <client-only>
            <ArrowPathIcon v-if="loading" class="animate-spin" :class="iconSizeClass" />
            <div v-else>
                <MiscIcon
                    v-if="isOnWishlist"
                    name="wishlist-checked"
                    :size="props.iconSize"
                />
                <MiscIcon
                    v-else
                    name="wishlist"
                    :size="props.iconSize"
                />
            </div>
        </client-only>
    </button>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { ArrowPathIcon } from '@heroicons/vue/24/outline'
import { useWishlist, useNuxtApp } from '#imports'
import type { HblProduct } from '@/utils/types'
import {useI18n} from "vue-i18n";

const props = defineProps<{
    product: HblProduct,
    text?: string,
    textPosition?: string,
    iconSize: string
}>()

const iconSizeClass = reactive({
    'w-3 h-3': props.iconSize === 'xs',
    'w-4 h-4': props.iconSize === 'sm',
    'w-5 h-5': props.iconSize === 'md',
    'w-6 h-6': props.iconSize === 'lg',
    'w-8 h-8': props.iconSize === 'xl',
    'w-10 h-10': props.iconSize === '2xl',
    '': props.size === 'none',
})

const { t } = useI18n()
const wishlistStore = useWishlist()
const { miniWishlist, error } = storeToRefs(wishlistStore)
const { addToWishlist, removeFromWishlist } = wishlistStore
const loading = ref(false)

const isOnWishlist = computed(() => {
    return miniWishlist.value?.includes(props.product.id)
})

const { $hblBus } = useNuxtApp()
async function toggleWishlist () {
    loading.value = true
    if (!miniWishlist.value?.includes(props.product.id)) {
        await addToWishlist(props.product.id)

        if (!error.value) {
            $hblBus.$emit('addToWishlist', { product: props.product })
        }
    } else {
        await removeFromWishlist(props.product.id)

        if (!error.value) {
            $hblBus.$emit('removeFromWishlist', { product: props.product })
        }
    }
    loading.value = false
}

const toggleText = computed(() => {
    if(props.textPosition) {
        if(props.text) {
            return props.text
        }

        if(isOnWishlist.value) {
            return t('product.card.wishlist.remove')
        }

        return t('product.card.wishlist.add')
    }

    return false
})
</script>
